// ABOUTME: Hook to fetch model selection data via POST. Products use /api/model-selection/, services use /api/model-selection/service-contract.
// ABOUTME: Returns data, loading, error states, and lazy-loading callbacks for subranges, search, and filters.

import type { ICategory, IFilter, IModelSelectionData } from '@coolshop/cool-model-selection';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useLanguageCtx } from '../../language';
import { ITEM_TYPE, ItemType } from '../types';
import { resolveCatalogLanguage } from './resolveCatalogLanguage';

// Matches IRangeSelection of @coolshop/cool-model-selection >= 3.9; kept local so the hook also
// compiles against the previous package line, where numeric facets simply never fire.
export type RangeSelection = { lower?: number; upper?: number };

/**
 * Option facets travel with an `opt:`-prefixed code so a content type sharing a feature code cannot
 * collide in the sidebar's flat filter-state map; the server wants the feature codes unprefixed
 * inside `optionFilters`. This splits the sidebar selection into the two payloads.
 */
export const OPTION_FILTER_CODE_PREFIX = 'opt:';

export function splitFilterSelection(
    selectedFilters: Record<string, string[]> = {},
    selectedRanges: Record<string, RangeSelection> = {},
): {
    contentFilters: Record<string, string[]>;
    optionFilters?: {
        options?: Record<string, string[]>;
        ranges?: Record<string, RangeSelection>;
    };
} {
    const contentFilters: Record<string, string[]> = {};
    const options: Record<string, string[]> = {};
    Object.entries(selectedFilters).forEach(([code, values]) => {
        if (!values.length) {
            return;
        }
        if (code.startsWith(OPTION_FILTER_CODE_PREFIX)) {
            options[code.slice(OPTION_FILTER_CODE_PREFIX.length)] = values;
        } else {
            contentFilters[code] = values;
        }
    });
    const ranges: Record<string, RangeSelection> = {};
    Object.entries(selectedRanges).forEach(([code, range]) => {
        if (range.lower === undefined && range.upper === undefined) {
            return;
        }
        const featureCode = code.startsWith(OPTION_FILTER_CODE_PREFIX)
            ? code.slice(OPTION_FILTER_CODE_PREFIX.length)
            : code;
        ranges[featureCode] = range;
    });

    const hasOptions = Object.keys(options).length > 0;
    const hasRanges = Object.keys(ranges).length > 0;
    if (!hasOptions && !hasRanges) {
        return { contentFilters };
    }
    return {
        contentFilters,
        optionFilters: {
            ...(hasOptions ? { options } : {}),
            ...(hasRanges ? { ranges } : {}),
        },
    };
}

async function fetchModelSelection(
    endpoint: string,
    body: Record<string, unknown> = {},
): Promise<Response> {
    return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}

export function useModelSelectionData(itemType?: ItemType, restrictToProductIds?: string[]) {
    const [data, setData] = useState<IModelSelectionData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // When set, every catalog request carries the restriction so search, range drill-down and
    // content filters all stay inside the restricted catalog.
    const restriction = useMemo(
        () => (restrictToProductIds ? { productIds: restrictToProductIds } : {}),
        [restrictToProductIds],
    );

    // Services are served by a separate endpoint (its non-CGT branch builds the item_type='service'
    // catalog via createModelSelectionDocumentService); products use the standard catalog endpoint.
    const endpoint = useMemo(
        () =>
            itemType === ITEM_TYPE.service
                ? '/api/model-selection/service-contract'
                : '/api/model-selection/',
        [itemType],
    );

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchModelSelection(endpoint, { ...restriction });
            if (!response.ok) {
                throw new Error(`Failed to fetch model selection data: ${response.statusText}`);
            }
            const json = await response.json();
            const categories: ICategory[] = json.categories ?? json ?? [];

            const modelSelectionData: IModelSelectionData = {
                id: 'cpq-prototype-selection',
                categories,
                fdps: [],
                mainFdps: [],
                techTypeToModelId: {},
                description: null,
            };

            setData(modelSelectionData);
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Failed to load model selection data';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [endpoint, restriction]);

    const loadSubranges = useCallback(
        async ({ range }: { range: ICategory }): Promise<ICategory[] | undefined> => {
            try {
                const response = await fetchModelSelection(endpoint, {
                    range: range.id,
                    ...restriction,
                });
                if (!response.ok) return undefined;
                const json = await response.json();
                return (json as ICategory[]) ?? undefined;
            } catch {
                return undefined;
            }
        },
        [endpoint, restriction],
    );

    const loadSearchResults = useCallback(
        async ({ searchText }: { searchText: string }): Promise<ICategory[] | undefined> => {
            try {
                const response = await fetchModelSelection(endpoint, {
                    searchText,
                    ...restriction,
                });
                if (!response.ok) return undefined;
                const json = await response.json();
                return (json as ICategory[]) ?? undefined;
            } catch {
                return undefined;
            }
        },
        [endpoint, restriction],
    );

    // Facet names and option labels are worded by the server in this PIM language: the tenant
    // language matching the browser locale, or the tenant default.
    const { locale } = useIntl();
    const { languages, defaultLanguage } = useLanguageCtx();
    const language = useMemo(
        () =>
            resolveCatalogLanguage(
                locale,
                (languages ?? []).map(entry => entry.isoCode),
                defaultLanguage,
            ),
        [locale, languages, defaultLanguage],
    );

    // Filters use the shared product-catalog endpoint regardless of itemType: the service-contract
    // router does not expose a separate /filters path, and the filter taxonomy is common to both
    // product and service catalogs. The current selection is forwarded so option facets narrow
    // (availability flags, numeric envelopes). Option facets only exist inside a family, so they are
    // requested only when a category is set: at the root the call is the plain content-type one.
    // The request does not depend on the tenant settings: the server adds option facets only when
    // the tenant has `optionFilters.enable`, so a content-type-only tenant gets its content-type
    // facets alone whatever this call asks for.
    const loadFilters = useCallback(
        async ({
            categoryId,
            selectedFilters,
            selectedRanges,
        }: {
            categoryId?: string;
            selectedFilters?: Record<string, string[]>;
            selectedRanges?: Record<string, RangeSelection>;
        }): Promise<IFilter[] | undefined> => {
            try {
                const { optionFilters } = splitFilterSelection(selectedFilters, selectedRanges);
                const response = await fetch('/api/model-selection/filters', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...(categoryId ? { categoryId, includeOptionFilters: true } : {}),
                        ...(categoryId && optionFilters ? { optionFilters } : {}),
                        ...(language ? { language } : {}),
                    }),
                });
                if (!response.ok) return undefined;
                const json = await response.json();
                // `json.degraded` is ignored on purpose: by product decision the catalog does not
                // surface fail-open results to the user. The flag is a diagnostic for the server
                // logs and the MCP tools (docs/developer/model-selection-option-filters.md, "The
                // fail-open contract").
                return json.filters ?? undefined;
            } catch {
                return undefined;
            }
        },
        [language],
    );

    const fetchWithFilters = useCallback(
        async (
            filters: Record<string, string[]>,
            parentCategoryId?: string,
            ranges?: Record<string, RangeSelection>,
        ): Promise<ICategory[] | undefined> => {
            try {
                const { contentFilters, optionFilters } = splitFilterSelection(filters, ranges);
                const response = await fetchModelSelection(endpoint, {
                    filters: contentFilters,
                    ...(optionFilters ? { optionFilters } : {}),
                    parentCategoryId,
                    ...restriction,
                });
                if (!response.ok) return undefined;
                const json = await response.json();
                // `json.degraded` is ignored on purpose, as in loadFilters above.
                return json.categories ?? json ?? [];
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('fetchWithFilters Error:', err);
                return undefined;
            }
        },
        [endpoint, restriction],
    );

    return {
        data,
        loading,
        error,
        fetchData,
        loadSubranges,
        loadSearchResults,
        loadFilters,
        fetchWithFilters,
    };
}
